import React, { useState, useEffect,useRef, useMemo } from 'react';
import { io } from 'socket.io-client';
import ContactListView from './ContactListView';
import ConversationView from './ConversationView';
import http from '../../config/http';

// ============================================================================
// UTILITIES
// ============================================================================
export const getInitials = (name) => {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const getAvatarColor = (id) => {
  const colors = [
    'bg-blue-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-green-500',
    'bg-indigo-500',
    'bg-red-500',
  ];
  // Handle both string and number IDs
  const numericId = typeof id === 'string' ? parseInt(id.slice(-1), 16) : id;
  return colors[numericId % colors.length];
};

export const formatTime = (date) => {
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

export const formatContactDate = (date) => {
  if (!date) return 'No messages yet';
  
  const now = new Date();
  const messageDate = new Date(date);
  const diffMs = now - messageDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return messageDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ============================================================================
// DUMMY DATA
// ============================================================================
export const DUMMY_CONTACTS = [
  { _id: '1', name: 'Sarah Johnson', email: 'sarah@example.com', phone: '+1234567890', status: 'online', lastMessage: 'Sounds good!', lastMessageDate: new Date(Date.now() - 3300000) },
  { _id: '2', name: 'Mike Chen', email: 'mike@example.com', phone: '+1234567891', status: 'online', lastMessage: 'Let me check that', lastMessageDate: new Date(Date.now() - 6900000) },
  { _id: '3', name: 'Emma Davis', email: 'emma@example.com', phone: '+1234567892', status: 'away', lastMessage: 'See you tomorrow', lastMessageDate: new Date(Date.now() - 86000000) },
  { _id: '4', name: 'John Smith', email: 'john@example.com', phone: '+1234567893', status: 'offline', lastMessage: 'Perfect, thanks!', lastMessageDate: new Date(Date.now() - 172800000) },
  { _id: '5', name: 'Lisa Wong', email: 'lisa@example.com', phone: '+1234567894', status: 'online', lastMessage: 'Great presentation!', lastMessageDate: new Date(Date.now() - 259200000) },
  { _id: '6', name: 'Alex Martinez', email: 'alex@example.com', phone: '+1234567895', status: 'offline', lastMessage: null, lastMessageDate: null },
  { _id: '7', name: 'Rachel Green', email: 'rachel@example.com', phone: '+1234567896', status: 'online', lastMessage: 'Thanks!', lastMessageDate: new Date(Date.now() - 600000) },
];

export const DUMMY_MESSAGES = {
  '1': [
    { id: 1, name: 'Sarah Johnson', type: 'text', text: 'Hey! How are you doing?', createdDate: new Date(Date.now() - 3600000), incoming: true, sense: 'incoming', channel: 'Whatsapp' },
    { id: 2, type: 'text', text: 'Doing great, thanks for asking!', createdDate: new Date(Date.now() - 3500000), incoming: false, sense: 'outgoing', channel: 'Whatsapp' },
    { id: 3, name: 'Sarah Johnson', type: 'image', text: 'https://via.placeholder.com/250x200?text=Project+Screenshot', createdDate: new Date(Date.now() - 3400000), incoming: true, sense: 'incoming', channel: 'Whatsapp' },
    { id: 4, name: 'Sarah Johnson', type: 'text', text: 'Just finished the design mockups', createdDate: new Date(Date.now() - 3350000), incoming: true, sense: 'incoming', channel: 'Whatsapp' },
    { id: 5, type: 'text', text: 'Sounds good!', createdDate: new Date(Date.now() - 3300000), incoming: false, sense: 'outgoing', channel: 'Whatsapp' },
  ],
  '2': [
    { id: 1, name: 'Mike Chen', type: 'text', text: 'Are we still on for the meeting?', createdDate: new Date(Date.now() - 7200000), incoming: true, sense: 'incoming', channel: 'Whatsapp' },
    { id: 2, type: 'text', text: 'Yes, 3 PM works for me', createdDate: new Date(Date.now() - 7100000), incoming: false, sense: 'outgoing', channel: 'Whatsapp' },
    { id: 3, name: 'Mike Chen', type: 'file', text: 'https://example.com/proposal.pdf', fileName: 'proposal.pdf', fileSize: '2.4 MB', createdDate: new Date(Date.now() - 7000000), incoming: true, sense: 'incoming', channel: 'Whatsapp' },
    { id: 4, name: 'Mike Chen', type: 'text', text: 'Let me check that', createdDate: new Date(Date.now() - 6900000), incoming: true, sense: 'incoming', channel: 'Whatsapp' },
  ],
  '3': [
    { id: 1, type: 'text', text: 'How was the event?', createdDate: new Date(Date.now() - 86400000), incoming: false, sense: 'outgoing', channel: 'Whatsapp' },
    { id: 2, name: 'Emma Davis', type: 'image', text: 'https://via.placeholder.com/250x200?text=Event+Photo', createdDate: new Date(Date.now() - 86300000), incoming: true, sense: 'incoming', channel: 'Whatsapp' },
    { id: 3, name: 'Emma Davis', type: 'text', text: 'It was amazing! Had a great time', createdDate: new Date(Date.now() - 86200000), incoming: true, sense: 'incoming', channel: 'Whatsapp' },
    { id: 4, type: 'text', text: 'That looks fun!', createdDate: new Date(Date.now() - 86100000), incoming: false, sense: 'outgoing', channel: 'Whatsapp' },
    { id: 5, name: 'Emma Davis', type: 'text', text: 'See you tomorrow', createdDate: new Date(Date.now() - 86000000), incoming: true, sense: 'incoming', channel: 'Whatsapp' },
  ],
};

export const MESSAGE_TEMPLATES = [
  '👍 Sounds good!',
  '✅ Got it, thanks!',
  '⏰ Let me check and get back to you',
  '📞 Let\'s hop on a call',
  '💯 Perfect!',
];

// ============================================================================
// MAIN APP COMPONENT
// ============================================================================
export default function MessagingApp({ contacts }) {
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [messages, setMessages] = useState(DUMMY_MESSAGES);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // 👇 keep contacts in state so we can reorder + set unread + last message
  const [contactsState, setContactsState] = useState(contacts || DUMMY_CONTACTS);

  // keep in sync if parent passes updated contacts
  useEffect(() => {
    if (contacts && Array.isArray(contacts)) setContactsState(contacts);
  }, [contacts]);

  // auto-select first contact
  useEffect(() => {
    if (!selectedContactId && contactsState.length > 0) {
      setSelectedContactId(contactsState[0]._id || contactsState[0].id);
    }
  }, [contactsState, selectedContactId]);

  // -------------------------
  // SOCKET SETUP
  // -------------------------
  const socketRef = useRef(null);

  const authToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

  useEffect(() => {
    if (!authToken) {
      console.warn('⚠️ Socket not connecting: missing authentication token');
      return;
    }

    // create socket once
    const socket = io(process.env.REACT_APP_API_URL || 'http://localhost:3000', {
      auth: { token: authToken },
      transports: ['websocket'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ socket connected', socket.id);
    });

    socket.on('connect_error', (err) => {
      console.error('❌ socket connect_error', err.message);
    });

    // MAIN EVENT: new message (incoming/outgoing)
    socket.on('message:new', (payload) => {
      // Expect payload to have at least: contactId, text, type, createdAt (or createdDate), sense
      const contactId = payload.contactId || payload.customerid;
      if (!contactId) return;

      const created = payload.createdAt || payload.createdDate || new Date();
      const previewText =
        payload.type && payload.type !== 'text'
          ? `[${payload.type}]`
          : (payload.text || '').slice(0, 80);

      // 1) Update contact list: lastMessage, lastMessageDate, unread, move to top
      setContactsState((prev) => {
        const idx = prev.findIndex((c) => (c._id || c.id) === contactId);
        if (idx === -1) return prev; // optionally you can refetch contacts

        const updated = [...prev];
        const existing = updated[idx];

        const isActive = String(contactId) === String(selectedContactId);
        const unread = isActive ? (existing.unread || 0) : (existing.unread || 0) + 1;

        const newContact = {
          ...existing,
          lastMessage: previewText,
          lastMessageDate: new Date(created),
          unread,
        };

        // move to top
        updated.splice(idx, 1);
        return [newContact, ...updated];
      });

      // 2) Store message in memory state for that contact
      setMessages((prev) => {
        const list = prev[contactId] || [];

        // Basic dedupe if server sends messageId/_id
        const incomingId = payload._id || payload.messageId;
        if (incomingId && list.some((m) => (m._id || m.messageId || m.id) === incomingId)) {
          return prev;
        }

        const normalized = {
          ...payload,
          createdDate: new Date(created),
        };

        return {
          ...prev,
          [contactId]: [...list, normalized],
        };
      });

      // 3) If not active chat, you might show a toast/badge (optional)
      // if (String(contactId) !== String(selectedContactId)) toast("New message...");
    });

    return () => {
      socket.off('message:new');
      socket.disconnect();
      socketRef.current = null;
    };
  }, [authToken, selectedContactId]);

  // -------------------------
  // FETCH MESSAGES ON SELECT
  // -------------------------
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedContactId || selectedContactId === 'null' || selectedContactId === 'undefined') return;

      try {
        setLoadingMessages(true);
        const response = await http.get(`/api/messages?customerid=${selectedContactId}`);

        setMessages((prev) => ({
          ...prev,
          [selectedContactId]: response.data || [],
        }));

        // when opening a chat, clear unread
        setContactsState((prev) =>
          prev.map((c) => {
            const cid = c._id || c.id;
            if (String(cid) !== String(selectedContactId)) return c;
            return { ...c, unread: 0 };
          })
        );
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [selectedContactId]);

  const selectedContact = useMemo(
    () => contactsState.find((c) => (c._id || c.id) === selectedContactId),
    [contactsState, selectedContactId]
  );

  const currentMessages = messages[selectedContactId] || [];

  const handleSendMessage = async (messageData) => {
    const newMessage = {
      text: messageData.text,
      type: messageData.type || null,
      sense: 'outgoing',
      createdDate: new Date(),
      incoming: false,
      channel: messageData.channel || 'Whatsapp',
      customerid: selectedContactId,
      ...(messageData.fileName && { fileName: messageData.fileName }),
      ...(messageData.fileSize && { fileSize: messageData.fileSize }),
    };

    // optimistic UI
    setMessages((prev) => ({
      ...prev,
      [selectedContactId]: [...(prev[selectedContactId] || []), newMessage],
    }));

    // update contact list preview immediately
    setContactsState((prev) => {
      const idx = prev.findIndex((c) => (c._id || c.id) === selectedContactId);
      if (idx === -1) return prev;

      const updated = [...prev];
      const existing = updated[idx];
      const newContact = {
        ...existing,
        lastMessage: (newMessage.type && newMessage.type !== 'text') ? `[${newMessage.type}]` : (newMessage.text || '').slice(0, 80),
        lastMessageDate: new Date(),
      };
      updated.splice(idx, 1);
      return [newContact, ...updated];
    });

    try {
      await http.post('/api/messages', newMessage);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-950 text-gray-900 dark:text-white">
      <ContactListView
        contacts={contactsState}
        selectedContactId={selectedContactId}
        onSelectContact={setSelectedContactId}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        loadingMessages={loadingMessages}
      />
      <ConversationView
        contact={selectedContact}
        messages={currentMessages}
        onSendMessage={handleSendMessage}
      />
    </div>
  );
}
